import { Component, ElementRef, OnDestroy, afterNextRender, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, from, race, take, timer } from 'rxjs';
import { ScrollTrigger, gsap } from '../../../../../motion/gsap';
import { QUERIES } from '../../../../../motion/motion-env';
import { MotionService } from '../../../../../motion/motion.service';
import { langLink, translateLive } from '../../../../../services/i18n-live';

interface HeroParts {
  section: HTMLElement;
  media: HTMLElement;
  camera: HTMLElement;
  img: Element;
  streaks: HTMLElement;
  textWrap: HTMLElement;
  sheen: HTMLElement;
  cta: HTMLElement;
  cue: HTMLElement;
}

/** Scroll speed (px/s) at which the light streaks reach their full stretch. */
const STREAK_FULL_SPEED = 3000;
const STREAK_REST_OPACITY = 0.45;

/**
 * The exact branded photograph is clipped into an independent car plane over a CSS road.
 * Ignition settles the scene, a fine pointer moves the camera and scrolling advances the car.
 * Controls remain visible through the intro. Reduced motion shows the static composition.
 */
@Component({
  selector: 'app-home-hero',
  templateUrl: './home-hero.component.html',
  styleUrl: './home-hero.component.css',
})
export class HomeHeroSectionComponent implements OnDestroy {
  readonly motion = inject(MotionService);
  private readonly translate = inject(TranslateService);
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private mm?: ReturnType<typeof gsap.matchMedia>;
  /** The ignition plays once per visit: a breakpoint or motion-preference change re-runs only the scroll effects. */
  private ignited = false;

  constructor() {
    afterNextRender(() => this.motion.outside(() => this.setup()));
  }

  ngOnDestroy(): void {
    this.mm?.revert();
  }

  /** Smooth-scrolls to #contact in the current language; modified clicks keep the link's default (new tab, etc.). */
  scrollToContact(event: MouseEvent): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    this.motion.scrollToSection('contact', event);
  }

  scrollToAbout(event: MouseEvent): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    this.motion.scrollToSection('about', event);
  }

  link(route: string): string {
    return langLink(this.translate, route);
  }

  private setup(): void {
    const parts = this.parts();
    if (!parts) return;
    // Under reduced motion the static end state is shown; a later switch to motion must not replay the intro.
    this.ignited = this.motion.reduced();

    const mm = (this.mm = gsap.matchMedia(this.host));
    mm.add({ motion: QUERIES.motion, mobile: QUERIES.mobile, fine: QUERIES.fine }, (ctx, contextSafe) => {
      const conditions = ctx.conditions!;
      if (!conditions['motion']) return;
      const mobile = !!conditions['mobile'];

      // A language switch re-creates the page: only the headline re-types (SplitTextDirective).
      const replay = this.ignited || this.motion.recentLanguageSwitch();
      this.ignited = true;
      const stopIgnition = replay ? null : this.ignite(parts, mobile, ctx, contextSafe!);
      const stopScroll = this.followCam(parts, mobile);
      const stopCamera = !mobile && conditions['fine'] ? this.cameraDepth(parts) : null;
      const pause = new IntersectionObserver(([entry]) => parts.section.classList.toggle('scene-paused', !entry.isIntersecting));
      pause.observe(parts.section);
      return () => {
        stopIgnition?.();
        stopScroll();
        stopCamera?.();
        pause.disconnect();
        parts.section.classList.remove('scene-paused');
      };
    });
  }

  /** A small perspective move on the car plane leaves the source artwork untouched. */
  private cameraDepth(p: HeroParts): () => void {
    gsap.set(p.camera, { transformPerspective: 1600, transformOrigin: '50% 70%' });
    const x = gsap.quickTo(p.camera, 'x', { duration: 0.9, ease: 'power3.out' });
    const y = gsap.quickTo(p.camera, 'y', { duration: 0.9, ease: 'power3.out' });
    const yaw = gsap.quickTo(p.camera, 'rotationY', { duration: 0.9, ease: 'power3.out' });
    const pitch = gsap.quickTo(p.camera, 'rotationX', { duration: 0.9, ease: 'power3.out' });
    const move = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      const rect = p.section.getBoundingClientRect();
      const dx = Math.max(-0.5, Math.min(0.5, (event.clientX - rect.left) / rect.width - 0.5));
      const dy = Math.max(-0.5, Math.min(0.5, (event.clientY - rect.top) / rect.height - 0.5));
      x(dx * 28);
      y(dy * 12);
      yaw(dx * 2.4);
      pitch(dy * -1.6);
    };
    const reset = () => { x(0); y(0); yaw(0); pitch(0); };
    p.section.addEventListener('pointermove', move, { passive: true });
    p.section.addEventListener('pointerleave', reset);
    return () => {
      p.section.removeEventListener('pointermove', move);
      p.section.removeEventListener('pointerleave', reset);
      [x, y, yaw, pitch].forEach((tween) => tween.tween.kill());
    };
  }

  private ignite(p: HeroParts, mobile: boolean, ctx: gsap.Context, contextSafe: gsap.ContextSafeFunc): () => void {
    // From-states, applied before the first paint.
    gsap.set(p.media, { scale: mobile ? 1.04 : 1.1, transformOrigin: '50% 75%' });
    gsap.set(p.cta, { y: 12 });
    gsap.set(p.cue, { opacity: 0 });

    const play = contextSafe(() => {
      if (ctx.isReverted) return;
      gsap
        .timeline()
        .to(p.media, { scale: 1, duration: 1.8, ease: 'expo.out', clearProps: 'transform,transformOrigin' }, 0)
        .to(p.cta, { y: 0, duration: 0.6, ease: 'power3.out', clearProps: 'transform' }, 0.35)
        // x: 0 overrides the px offset GSAP would parse from the CSS translateX(-120%).
        .fromTo(p.sheen, { x: 0, xPercent: -120 }, { xPercent: 340, duration: 1.1, ease: 'power2.inOut', clearProps: 'transform' }, 1.35)
        .to(p.cue, { opacity: 1, duration: 0.4, clearProps: 'opacity' }, 1.6);
    }) as () => void;

    // Start together with the headline words: once fonts and the first translation are in (1.2 s at most).
    const ready = forkJoin([from(this.motion.fontsReady), translateLive<string>(this.translate, 'home.welcome.text-1').pipe(take(1))]);
    // The translation can arrive inside the Angular zone, so the timeline is created outside it.
    const sub = race(ready, timer(1200))
      .pipe(take(1))
      .subscribe(() => this.motion.outside(play));
    return () => sub.unsubscribe();
  }

  private followCam(p: HeroParts, mobile: boolean): () => void {
    const stretch = gsap.quickTo(p.streaks, 'scaleX', { duration: 0.35, ease: 'power2.out' });
    const glow = gsap.quickTo(p.streaks, 'opacity', { duration: 0.35, ease: 'power2.out' });
    const rest = () => {
      stretch(1);
      glow(STREAK_REST_OPACITY);
    };

    gsap
      .timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: p.section,
          // From the first scrolled pixel (the hero sits right under the fixed navbar).
          start: 0,
          end: 'bottom top',
          scrub: true,
          onUpdate: (self) => {
            const speed = Math.abs(self.getVelocity()) / STREAK_FULL_SPEED;
            stretch(1 + Math.min(speed, 1.1));
            glow(STREAK_REST_OPACITY + Math.min(speed, 1) * 0.4);
          },
        },
      })
      .to(p.img, { xPercent: mobile ? 2 : 6, scale: mobile ? 1.02 : 1.06, transformOrigin: '50% 70%', duration: 1 }, 0)
      .to(p.textWrap, { y: mobile ? -32 : -64, duration: 1 }, 0);
    ScrollTrigger.addEventListener('scrollEnd', rest);

    const io = new IntersectionObserver(([entry]) => p.streaks.classList.toggle('is-paused', !entry.isIntersecting));
    io.observe(p.section);

    ScrollTrigger.create({
      start: 40,
      end: 'max',
      onToggle: (self) => p.cue.classList.toggle('is-gone', self.isActive),
    });

    return () => {
      ScrollTrigger.removeEventListener('scrollEnd', rest);
      io.disconnect();
      p.streaks.classList.remove('is-paused');
      p.cue.classList.remove('is-gone');
    };
  }

  private parts(): HeroParts | null {
    const get = (selector: string) => this.host.querySelector<HTMLElement>(selector);
    const parts = {
      section: get('#home'),
      media: get('.hero-media'),
      camera: get('.hero-camera'),
      img: this.host.querySelector('.hero-img'),
      streaks: get('.hero-streaks'),
      textWrap: get('.hero-text-wrap'),
      sheen: get('.hero-sheen'),
      cta: get('.hero-cta-wrap'),
      cue: get('.hero-cue'),
    };
    return Object.values(parts).every(Boolean) ? (parts as HeroParts) : null;
  }
}
